import { useState, useRef, useEffect } from "react";
import { Alert } from "@mui/material";
import Modal from "../../../../components/Modal/Modal";
import DefaultButton from "../../../../components/simple/DefaultButton";
import { useAPI } from "../../../../hooks/useAPI";
import { useAuth } from "../../../../contexts/AuthContext";

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  currentEmail: string;
  prefilledNewEmail?: string;
};

type Step = "verify_current" | "confirm_new" | "success";

const EmailChangeModal = ({
  open,
  onClose,
  onSuccess,
  currentEmail,
  prefilledNewEmail = "",
}: Props) => {
  const { callAPI } = useAPI();
  const { authToken } = useAuth();

  const [step, setStep] = useState<Step>("verify_current");
  const [changeToken, setChangeToken] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [maskedDest, setMaskedDest] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const resetState = () => {
    setStep("verify_current");
    setChangeToken("");
    setNewEmail("");
    setMaskedDest("");
    setCode(["", "", "", "", "", ""]);
    setError("");
    setLoading(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    resetState();
    if (prefilledNewEmail) setNewEmail(prefilledNewEmail);
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  }, [open]);

  // Kick off step 1 when the modal opens (send to current email)
  useEffect(() => {
    if (!open) return;
    resetState();
    if (prefilledNewEmail) setNewEmail(prefilledNewEmail);
    setLoading(true);
    callAPI("user/request_email_change", { method: "POST", token: authToken })
      .then((res: any) => {
        setChangeToken(res.change_token);
        setMaskedDest(res.code_sent_to || currentEmail);
      })
      .catch((err: any) => {
        setError(err?.message || "Failed to send verification code.");
      })
      .finally(() => setLoading(false));
  }, [open]);

  const handleCodeChange = (index: number, value: string) => {
    if (value !== "" && !/^[0-9]$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
    if (newCode.every(Boolean) && index === 5) {
      submitCode(newCode.join(""));
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === "Backspace" && !e.currentTarget.value && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    }
  };

  const submitCode = async (codeStr: string) => {
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      if (step === "verify_current") {
        const res = await callAPI("user/verify_current_email_for_change", {
          method: "POST",
          token: authToken,
          body: {
            change_token: changeToken,
            verification_code: codeStr,
            new_email: newEmail,
          },
        });
        setChangeToken(res.change_token);
        setMaskedDest(res.code_sent_to || newEmail);
        setCode(["", "", "", "", "", ""]);
        setStep("confirm_new");
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      } else if (step === "confirm_new") {
        await callAPI("user/confirm_new_email", {
          method: "POST",
          token: authToken,
          body: { change_token: changeToken, verification_code: codeStr },
        });
        setStep("success");
      }
    } catch (err: any) {
      setError(err?.message || "Invalid or expired verification code.");
      setCode(["", "", "", "", "", ""]);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = () => {
    const codeStr = code.join("");
    if (codeStr.length === 6) submitCode(codeStr);
  };

  const stepTitle =
    step === "verify_current"
      ? "Verify current email"
      : step === "confirm_new"
        ? "Verify new email"
        : "Email updated";

  const stepDescription =
    step === "verify_current"
      ? `Enter the code sent to ${maskedDest}`
      : step === "confirm_new"
        ? `Enter the code sent to ${maskedDest}`
        : "Your email address has been updated successfully.";

  return (
    <Modal
      id="email-change-modal"
      title="Change Email Address"
      open={open}
      onClose={handleClose}
      showClose
      slim
    >
      <strong style={{ fontSize: "14px" }}>{stepTitle}</strong>
      <p style={{ margin: "5px 0 16px 0", fontSize: "14px" }}>
        {stepDescription}
      </p>

      {step === "verify_current" && (
        <div style={{ marginBottom: "16px" }}>
          <strong style={{ fontSize: "14px" }}>New Email Address</strong>
          <p
            style={{
              margin: "5px 0 16px 0",
              fontSize: "14px",
              color: "#535862",
            }}
          >
            {newEmail}
          </p>
        </div>
      )}

      {step !== "success" && (
        <>
          <div
            style={{ display: "flex", gap: "8px", marginBottom: "16px" }}
            className="code-inputs"
          >
            {code.map((digit, i) => (
              <input
                key={i}
                type="tel"
                maxLength={1}
                value={digit}
                onChange={(e) => handleCodeChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                ref={(el) => (inputRefs.current[i] = el)}
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                disabled={loading}
                style={{
                  width: "40px",
                  height: "48px",
                  textAlign: "center",
                  fontSize: "20px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                }}
              />
            ))}
          </div>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <DefaultButton
            type="button"
            onClick={handleManualSubmit}
            disabled={loading || code.some((d) => !d)}
            wideOnMobile
            className="action-button"
          >
            {loading ? "Verifying..." : "Verify"}
          </DefaultButton>
        </>
      )}

      {step === "success" && (
        <DefaultButton
          type="button"
          onClick={() => {
            onSuccess();
            resetState();
          }}
          className="action-button"
        >
          Done
        </DefaultButton>
      )}
    </Modal>
  );
};

export default EmailChangeModal;
